import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { ColumnsType } from 'antd/es/table';

import { useBeneficiariesActions } from '@/entities/beneficiary';
import { useDocuments } from '@/entities/document';
import { PidType } from '@/entities/personalIdentifiers';
import { personalIdentifierFieldSorter } from '@/entities/personalIdentifiers/helpers/personalIdentifierFieldSorter';
import { Country } from '@/shared/components';
import { CollectionName, PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm';
import { compareStrings } from '@/shared/utils/strings';

import { EntityTableOptions } from '../types';

const EMPTY_TEXT = '-';

export const usePersonalIdsTableOptions = (): EntityTableOptions<PersonalIdentifierDocumentDto> => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'vfoImportDocument.columns' });
  const { getPersonalIds } = useDocuments();
  const { findById } = useBeneficiariesActions();

  const personalIds = useMemo(() => getPersonalIds(), []);

  const beneficiaryNamesByPersonalId = useMemo<Record<string, string>>(() => {
    const entries = personalIds.map(({ id, beneficiaryId }) => {
      if (!beneficiaryId) return [id, EMPTY_TEXT];
      const beneficiaryData = findById(beneficiaryId);
      if (!beneficiaryData) return [id, EMPTY_TEXT];
      const beneficiaryName = `${beneficiaryData.firstName} ${beneficiaryData.lastName}`;
      return [id, beneficiaryName];
    });

    return Object.fromEntries(entries);
  }, [personalIds, findById]);

  const columns = useMemo<ColumnsType<PersonalIdentifierDocumentDto>>(
    () => [
      {
        key: 'title',
        title: t('title'),
        render: (item: PersonalIdentifierDocumentDto) => item.name ?? EMPTY_TEXT,
        sorter: personalIdentifierFieldSorter.name,
      },
      {
        key: 'type',
        title: t('type'),
        width: 130,
        render: (item: PersonalIdentifierDocumentDto) => (
          <PidType type={item.personalIdentifierType} />
        ),
        sorter: personalIdentifierFieldSorter.type,
      },
      {
        key: 'country',
        title: t('country'),
        width: 113,
        render: (item: PersonalIdentifierDocumentDto) => <Country code={item.country} />,
        sorter: personalIdentifierFieldSorter.country,
      },
      {
        key: 'associatedWith',
        title: t('associatedWith'),
        width: 164,
        render: (item: PersonalIdentifierDocumentDto) => beneficiaryNamesByPersonalId[item.id],
        sorter: (a, b) =>
          compareStrings(beneficiaryNamesByPersonalId[a.id], beneficiaryNamesByPersonalId[b.id]),
      },
    ],
    [],
  );

  return {
    collectionName: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
    data: personalIds,
    columns,
  };
};
